// Angular
import { Component, EventEmitter, Input, OnDestroy, Output } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
// Store
import { Store } from '@ngxs/store';
// Service
import { RuleService } from '../service/rule.service';
import { OverviewSubHeaderService } from '../../overview/sub-header/service/overview-sub-header.service';
import { BroadcastService } from '@cv-invision/core';
// State
import { QualityState } from '../../state/quality.state';
import { RulesState } from '../state/rules.state';
// Model
import { RuleModel } from '../model/rule.model';
import { OrderDetailModel } from '../../state/model/order-detail.model';
import { ORDER_STATE, RULE_MESSAGE_TYPE, RULE_UPDATED_EVENT } from '../model/constants';
import { AddManualRule } from '../state/action/rules.actions';
import { LoggerService, ObjectUtils, UserContextState } from '@cv-invision/core';
import { RuleEngine } from '../model/rule-engine';
import { DOCUMENT_RULES_UI_SECTIONS } from '../../model/quality.constant';
import { RulesModel } from '../model/rules.model';

@Component({
	selector: 'cv-rule-create-component',
	templateUrl: './rule-create.component.html',
	styleUrls: ['./rule-create.component.scss']
})
export class RuleCreateComponent implements OnDestroy {
	@Input() targets: Array<string>;
	@Input() primaryTargets: Array<string>;
	@Output() cancel: EventEmitter<any> = new EventEmitter<any>();
	@Output() saved: EventEmitter<RuleModel | boolean> = new EventEmitter<RuleModel | boolean>();
	@Output() failed: EventEmitter<any> = new EventEmitter<any>();

	orderDetail$: Observable<OrderDetailModel> = this.store.select(QualityState.getOrderDetail);
	selectedSection$: Observable<any> = this.store.select(QualityState.getSelectedSection);
	orderRules$: Observable<any> = this.store.select(RulesState.getOrderRules);

	public orderDetailModel: OrderDetailModel;
	public orderRules: any;
	public saving: boolean;
	public title: string;
	public description: string;
	public status: RULE_MESSAGE_TYPE;

	public navItems: any = [];
	public selectedSection: any = null;
	public rulesModel: any;

	get lockedBy(): any {
		return this.subheaderService.orderLocked;
	}

	private atComponentDestroy = new Subject();

	constructor(
		private ruleService: RuleService,
		private store: Store,
		private loggerService: LoggerService,
		private broadcastService: BroadcastService,
		private subheaderService: OverviewSubHeaderService
	) {
		this.broadcastService.broadcast({
			name: RULE_UPDATED_EVENT,
			event: {
				type: 'update-rule-flag'
			}
		});

		this.rulesModel = new RulesModel();

		this.orderDetail$
			.pipe(takeUntil(this.atComponentDestroy))
			.subscribe((orderDetailModel: OrderDetailModel) => {
				this.orderDetailModel = orderDetailModel;
			});

		this.selectedSection$
			.pipe(takeUntil(this.atComponentDestroy))
			.subscribe((selectedSection: any) => {
				if (selectedSection) {
					// Get all Nav Items that belongs to the Quality Module
					this.loadSection();

					// Pre-select first option if something goes wrong
					if (this.selectedSection === null && this.navItems.length > 0) {
						this.selectedSection = this.navItems[0];
					}
				}
			});

		this.orderRules$.pipe(takeUntil(this.atComponentDestroy)).subscribe((orderRules: any) => {
			this.orderRules = orderRules;
		});

		this.status = RULE_MESSAGE_TYPE.Info;
		this.title = '';
		this.description = '';
		this.saving = false;
	}

	/**
	 * Prepares the Section and the Current Selected Section
	 * @param uiSection
	 */
	private loadSection(): void {
		if (RuleEngine.currentUISection !== DOCUMENT_RULES_UI_SECTIONS.REVISIONS) {
			// Get all Nav Items that belongs to the Quality Module
			this.navItems = this.store
				.selectSnapshot(UserContextState.getNavItems)
				.filter((navItem: any) => {
					// Match current section
					if (
						navItem.path &&
						(navItem.path.toUpperCase() === RuleEngine.currentUISection.toUpperCase() ||
							navItem.path.toUpperCase() ===
								ObjectUtils.removeStringWhitespace(RuleEngine.currentUISection.toUpperCase()))
					) {
						this.selectedSection = navItem;
					}
					// Can-t add rule on revisions section
					return navItem.module === 'quality' && navItem.path !== 'revisions';
				});
		}
	}

	/**
	 * Returns true if form is valid, otherwise false.
	 */
	isFormValid(): boolean {
		return (
			this.title &&
			this.title.trim().length > 0 &&
			this.description &&
			this.description.trim().length > 0 &&
			this.status &&
			this.status.trim().length > 0 &&
			this.selectedSection != null &&
			this.selectedSection.path.length > 0
		);
	}

	/**
	 * Creates the rule and calls the output event saved with the new rule created.
	 */
	createRule(): void {
		if (
			this.orderDetailModel.stateEnum === ORDER_STATE.REJECTED ||
			this.orderDetailModel.stateEnum === ORDER_STATE.APPROVED
		) {
			return;
		}
		this.saving = true;
		const orderId = this.orderDetailModel.id;
		const documentId = this.orderDetailModel.documentId;
		const sortId = this.getSortId();
		if (!sortId) {
			this.failed.emit();
			this.saving = false;
		}

		this.ruleService
			.createRule(
				orderId,
				documentId,
				this.status,
				this.selectedSection.path.toUpperCase(),
				sortId,
				this.title,
				this.description,
				this.targets,
				this.primaryTargets
			)
			.subscribe({
				next: (result: any) => {
					if (result.isValid && result.key) {
						this.ruleService.getRule(orderId, documentId, result.key).subscribe(getResult => {
							this.store.dispatch(new AddManualRule({ violation: getResult }));
							this.saved.emit(getResult);
							this.saving = false;
						});
					} else {
						this.failed.emit();
						this.saving = false;
					}
				},
				error: (error: any) => {
					this.saving = false;
				}
			});
	}

	/**
	 * Get the last custom rule sortID
	 */
	getSortId(): number {
		let sortId = this.getSchemaSortId();
		const rulesSection: Array<any> = (
			this.orderRules.violation.filter(v => {
				return (
					v.uiSection.toUpperCase().indexOf(this.selectedSection.path.toUpperCase()) > -1 &&
					v.cvSortId >= sortId
				);
			}) || []
		).sort((a: any, b: any) => a.cvSortId - b.cvSortId);

		if (rulesSection.length) {
			const lastIndex = rulesSection.length - 1;
			const newSortId = rulesSection[lastIndex].cvSortId;

			if (newSortId >= sortId) {
				sortId = newSortId + 1;
			}
		}

		return sortId;
	}

	/**
	 * Get the sort id from the schema
	 */
	private getSchemaSortId(): number {
		const schemaName = this.orderDetailModel.schema;

		if (!this.rulesModel.rules[schemaName]
			|| !this.rulesModel.rules[schemaName][this.selectedSection.path]
		) {
			this.loggerService.error(`schemaName not mapped ${schemaName}`);
			return 1;
		}

		const rulesBase = this.rulesModel.rules[schemaName][this.selectedSection.path].rulesBase;

		return 949 + rulesBase;
	}

	/**
	 * On Cancel/Close panel form.
	 */
	public onCancel(): void {
		this.cancel.emit();
	}

	/**
	 * Close all when the element is destroyed
	 * in case a Sub Component remains visible
	 */
	ngOnDestroy(): void {
		// Globally Destroy anything attached to it
		this.atComponentDestroy.next({});
		this.atComponentDestroy.unsubscribe();
	}
}
